import {Component, EventEmitter, HostListener, Inject, Input, OnInit, Output} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {AppComponent} from '../../app.component';
import {Breakpoints, MediaMatcher} from '@angular/cdk/layout';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {FS3Notification} from "../../objects/notification";
import {SharedDataService} from "../../services/shared-data.service";

@Component({
  selector: 'app-profile-password-change',
  templateUrl: './profile-password-change.component.html',
  styleUrls: ['../login-form/login-form.component.scss']
})
export class ProfilePasswordChangeComponent implements OnInit {
  @Input() application: AppComponent;
  @Output() close = new EventEmitter<void>();

  noMatchState = false;
  resetForm = new FormGroup({
    password: new FormControl('', [Validators.required, this.passwordValidator(), this.noMatchStateValidator()]),
    confirm_password: new FormControl('', [Validators.required, this.validatePasswordConfirm, this.noMatchStateValidator()]),
  });
  resetFormErrors = {
    'old_password': '',
    'password': '',
    'confirm_password': ''
  };

  @Input() displayMode = 'default';
  private _isOpened = false;
  @Input() set isOpened(value: boolean) {
    this._isOpened = value;
    if (this._isOpened) {
      this.checkScroll();
    }
  }

  state = 'default';
  hasContentScroll = false
  resetStatus = '';
  resetMessage = '';
  resetValidationMessages = {
    'old_password': {
      'required': 'Your old password is required',
      'nomatch': ''
    },
    'password': {
      'required': 'Password is required',
      'passwordInvalid': 'Your password must be between 8 and 25 characters long',
      'nomatch': ''
    },
    'confirm_password': {
      'required': 'Password confirmation is required',
      'validatePasswordConfirm': 'Passwords do not match'
    }
  };

  layoutMatches = {
    isHandsetDefaultLayout: false
  }

  @HostListener('window:resize', ['$event']) public onLayoutChange($event) {
    this.setLayoutMatches();
  }

  constructor(private mediaMatcher: MediaMatcher,
              private sharedDataService: SharedDataService) {
    this.setLayoutMatches();
  }

  private setLayoutMatches() {
    const handsetBreakPoints = Breakpoints.Handset.split(', ');
    let isHandset = false;
    handsetBreakPoints.forEach(media => {
      if (this.mediaMatcher.matchMedia(media).matches) {
        isHandset = true;
      }
    })
    if (isHandset) {
      this.toggleLayout('isHandsetDefaultLayout')
    } else {
      this.purgeLayout()
    }
  }

  private toggleLayout(key: string) {
    for ( const x in this.layoutMatches ) {
      if (x === key) {
        this.layoutMatches[x] = true;
      } else {
        this.layoutMatches[x] = false;
      }
    }
  }

  private purgeLayout() {
    for ( const x in this.layoutMatches ) {
      if (this.layoutMatches[x]) {
        this.layoutMatches[x] = false;
      }
    }
  }

  ngOnInit(): void {
    if (this.displayMode === 'popup') {
      const old_password: FormControl = new FormControl('', [Validators.required, this.noMatchStateValidator()]);
      this.resetForm.addControl('old_password', old_password);
    }
  }

  private passwordValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      if (!control.value) {
        return null
      }
      if (control.value.length < 8 || control.value.length > 25) {
        return {'passwordInvalid': true}
      }
      return null;
    };
  }

  private noMatchStateValidator(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      return this.noMatchState ? {'nomatch': true} : null;
    };
  }

  private checkScroll() {
    this.hasContentScroll = document.getElementById('form-content').scrollHeight > document.getElementById('form-content').clientHeight
  }

  public processPasswordChange(result) {
    this.application.isActiveRequest = false;
    if (!result || !result.success) {
      this.resetStatus = 'error';
      this.noMatchState = true;
      this.resetForm.controls['password'].updateValueAndValidity();
      this.resetForm.controls['confirm_password'].updateValueAndValidity();
      this.resetMessage = (result && result.message) ? result.message : '';
      setTimeout(() => { // check for additional css on content with possible scroll
        this.checkScroll()
      }, 1);
    } else {
      this.resetStatus = '';
      if (this.displayMode === 'default') {
        this.application.user.change_password = false; // update flag
        this.application.router.navigateByUrl('/files', { state: { passwordChanged: true } });
      } else {
        const notification = new FS3Notification()
        notification.text = 'Your password has been changed successfully.';
        notification.type = 'success';
        this.sharedDataService.pushNotification(notification);
        this.close.emit();
      }
    }
  }
  public showErrorOnPasswordChange(result) {
    this.application.isActiveRequest = false;
    // this.state = 'validating_error';
    this.resetStatus = 'error';
    this.resetMessage = (result && result.errMsg) ? result.errMsg : '';
    setTimeout(() => { // check for additional css on content with possible scroll
      this.checkScroll()
    }, 1);
  }

  public logout() {
    this.application.logout();
    this.application.router.navigateByUrl('/');
  }
  public onResetSubmit() {
    this.sharedDataService.pushNotification({ text: `This functionality will be available in two weeks time.`, type: 'info' })
    return;
    if (this.application.isActiveRequest) {
      return
    }
    this.resetMessage = '';
    if (this.noMatchState) {
      this.noMatchState = false;
      this.resetForm.controls['password'].updateValueAndValidity()
    }
    this.resetForm.controls['confirm_password'].updateValueAndValidity(); // ensure confirm_password will work
    this.updateResetFormErrorsForDisplay();
    if (this.resetForm.valid) {
      this.application.appError = '';
      const oldPwd = this.displayMode === 'popup' ? this.resetForm.controls['old_password'].value : '';
      this.doPasswordChange(this.resetForm.controls['password'].value, oldPwd);
    }
  }
  public updateResetFormErrorsForDisplay() {
    for (const field in this.resetFormErrors) {
      if (!this.resetForm.controls[field]) { continue; }
      this.resetFormErrors[field] = '';
      const control = this.resetForm.get(field);
      if (control && !control.valid) {
        const messages = this.resetValidationMessages[field];
        for (const key in control.errors) {
          if (control.errors.hasOwnProperty(key)) {
            this.resetFormErrors[field] = messages[key];
            // this.resetFormErrors[field]+= ' ' + messages[key];
            break; // display only one error message
          }
        }
      }
    }
  }

  validatePasswordConfirm(c: FormControl) {
    if (c.parent && c.parent.controls['password'].value !== c.value) {
      return { validatePasswordConfirm: false };
    } else {
      return null;
    }
  }

  public doPasswordChange(pwd: string, oldPwd: string = '') {
    this.application.isActiveRequest = true;
    this.application.apiService.doPasswordChange(pwd, oldPwd).subscribe(
      data => {
        this.processPasswordChange(data);
      },
      error =>
        this.showErrorOnPasswordChange(error)
    );
  }

  public onNoClick() {
    this.close.emit()
  }

}
